import { Form, Input, Row, Col, DatePicker, InputNumber, Select, Card, Button, Typography } from 'antd';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import FormPage from '../../../../../../components/pages/FormPage';
import moment from 'moment';
import { PlusOutlined, DeleteOutlined, ThunderboltOutlined } from '@ant-design/icons';
import kdKantor from '../../../../../../assets/kode_kantor.json';
import kdKemas from '../../../../../../assets/kode_kemas.json';

const DATE_FMT = 'DD-MM-YYYY';
const DATE_PICK_FMT = 'DD-MMM-YYYY';

const fmtDate = (d) => {
    if (!d) return '';
    const m = moment(d);
    return m.isValid() ? m.format(DATE_FMT) : '';
};

const DefKemasan = () => ({
    jenisKemasan: '',
    jumlahKemasan: '',
    nomorBlAwb: '',
    tanggalBlAwb: '',
});

const DefKontainer = () => ({
    nomorKontainer: '',
    ukuranKontainer: '',
});

const DefModel = {
    _id: null,
    // header
    alasan: '',
    kodeKantor: '050100',
    kodeTps: '',
    namaPemohon: '',
    nomorBc11: '',
    nomorPlp: '',
    nomorSurat: '',
    refNumber: '',
    tanggalBc11: '',
    tanggalPlp: '',
    tanggalSurat: '',
    tipeData: '',
    kemasan: [DefKemasan()],
    kontainer: [DefKontainer()],
    isCreate: true
};

// Contoh payload API untuk tombol "Isi Otomatis"
const SAMPLE_DATA = {
    header: {
        alasan: 'Barang sudah keluar dari TPS sebelum PLP diterbitkan',
        kodeKantor: '050100',
        kodeTps: 'UNX1',
        namaPemohon: 'ABDULLAH AZZAM',
        nomorBc11: '085891',
        nomorPlp: '000174',
        nomorSurat: '877231A',
        tanggalBc11: '28-09-2026',
        tanggalPlp: '02-10-2026',
        tanggalSurat: '28-09-2026',
        tipeData: '1',
    },
    kemasan: [
        {
            jenisKemasan: 'PK',
            jumlahKemasan: 5,
            nomorBlAwb: '406-93764506',
            tanggalBlAwb: '28-09-2026',
        },
    ],
    kontainer: [
        {
            nomorKontainer: '',
            ukuranKontainer: '',
        },
    ],
};

export default () => {
    const [model, setModel] = useState(DefModel);
    const { warehouseCache } = useSelector((state) => state.apps);
    const warehouseOpts = (warehouseCache || []).map((w) => ({
        value: w.kode_tps,
        label: `${w.kode_tps} - ${w.nama || ''}`.trim(),
    }));
    const kemasanOpts = (kdKemas || []).map((k) => ({
        value: (k.value || '').trim(),
        label: `${(k.value || '').trim()} - ${(k.name || '').trim()}`,
    }));

    const set = (patch) => setModel((prev) => ({ ...prev, ...patch }));

    const handleDateChange = (name, date) => {
        set({ [name]: date });
    };

    // ---- kemasan handlers ----
    const updateKemasan = (idx, patch) => {
        const list = [...(model.kemasan || [])];
        list[idx] = { ...list[idx], ...patch };
        set({ kemasan: list });
    };
    const addKemasan = () => set({ kemasan: [...(model.kemasan || []), DefKemasan()] });
    const removeKemasan = (idx) => {
        const list = (model.kemasan || []).filter((_, i) => i !== idx);
        set({ kemasan: list.length ? list : [DefKemasan()] });
    };

    // ---- kontainer handlers ----
    const updateKontainer = (idx, patch) => {
        const list = [...(model.kontainer || [])];
        list[idx] = { ...list[idx], ...patch };
        set({ kontainer: list });
    };
    const addKontainer = () => set({ kontainer: [...(model.kontainer || []), DefKontainer()] });
    const removeKontainer = (idx) => {
        const list = (model.kontainer || []).filter((_, i) => i !== idx);
        set({ kontainer: list.length ? list : [DefKontainer()] });
    };

    // mapping data edit -> bentuk form flat
    // API bisa mengembalikan bentuk { header, kemasan, kontainer } atau flat di root
    const toMoment = (v) => {
        if (!v) return null;
        if (moment.isMoment(v)) return v.isValid() ? v : null;
        const m = moment(v, [DATE_FMT, DATE_PICK_FMT, 'YYYYMMDD', moment.ISO_8601], true);
        return m.isValid() ? m : null;
    };
    const mapEditToForm = (s) => {
        if (!s) return s;
        const h = s.header || s;
        const kemasanSrc = (s.kemasan || h.kemasan || []);
        const kontainerSrc = (s.kontainer || h.kontainer || []);
        return {
            ...DefModel,
            _id: s._id || s.id || null,
            isCreate: false,
            alasan: h.alasan || s.alasan || '',
            kodeKantor: h.kodeKantor || s.kodeKantor || '',
            kodeTps: h.kodeTps || s.kodeTps || '',
            namaPemohon: h.namaPemohon || s.namaPemohon || '',
            nomorBc11: h.nomorBc11 || s.nomorBc11 || '',
            nomorPlp: h.nomorPlp || s.nomorPlp || '',
            nomorSurat: h.nomorSurat || s.nomorSurat || '',
            refNumber: h.refNumber || s.refNumber || '',
            tanggalBc11: toMoment(h.tanggalBc11 ?? s.tanggalBc11),
            tanggalPlp: toMoment(h.tanggalPlp ?? s.tanggalPlp),
            tanggalSurat: toMoment(h.tanggalSurat ?? s.tanggalSurat),
            tipeData: String(h.tipeData ?? s.tipeData ?? '0'),
            kemasan: kemasanSrc.length ? kemasanSrc.map((k) => ({
                jenisKemasan: k.jenisKemasan || '',
                jumlahKemasan: Number(k.jumlahKemasan ?? 0),
                nomorBlAwb: k.nomorBlAwb || '',
                tanggalBlAwb: toMoment(k.tanggalBlAwb),
            })) : [DefKemasan()],
            kontainer: kontainerSrc.length ? kontainerSrc.map((k) => {
                // API menyimpan "20", opsi form "20 FEET" — normalisasi untuk tampil
                const raw = String(k.ukuranKontainer ?? '').trim();
                return {
                    nomorKontainer: k.nomorKontainer || '',
                    ukuranKontainer: /^\d+$/.test(raw) ? `${raw} FEET` : raw,
                };
            }) : [DefKontainer()],
        };
    };

    // Isi otomatis form dari SAMPLE_DATA (pertahankan mode create/edit saat ini)
    // Nomor Surat selalu random 6 digit agar unik tiap klik
    const fillSample = () => {
        const mapped = mapEditToForm(SAMPLE_DATA);
        setModel((prev) => ({
            ...mapped,
            nomorSurat: String(Math.floor(Math.random() * 1e6)).padStart(6, '0'),
            _id: prev?._id ?? null,
            isCreate: prev?.isCreate ?? true,
        }));
    };

    const buildPayload = (m) => ({
        // sertakan _id saat mode Edit agar backend tahu record yang diupdate
        ...(m._id ? { _id: m._id } : {}),
        header: {
            alasan: m.alasan || '',
            kodeKantor: m.kodeKantor || '',
            kodeTps: (m.kodeTps || '').toUpperCase(),
            namaPemohon: (m.namaPemohon || '').toUpperCase(),
            nomorBc11: m.nomorBc11 || '',
            nomorPlp: m.nomorPlp || '',
            nomorSurat: m.nomorSurat || '',
            tanggalBc11: fmtDate(m.tanggalBc11),
            tanggalPlp: fmtDate(m.tanggalPlp),
            tanggalSurat: fmtDate(m.tanggalSurat),
            tipeData: String(m.tipeData ?? '0'),
        },
        kemasan: (m.kemasan || []).map((k) => ({
            jenisKemasan: (k.jenisKemasan || '').toUpperCase(),
            jumlahKemasan: Number(k.jumlahKemasan ?? 0),
            nomorBlAwb: k.nomorBlAwb || '',
            tanggalBlAwb: fmtDate(k.tanggalBlAwb),
        })),
        kontainer: (m.kontainer || []).map((k) => ({
            nomorKontainer: (k.nomorKontainer || '').toUpperCase(),
            // API hanya terima angka ("20"), bukan "20 FEET"
            ukuranKontainer: String(k.ukuranKontainer ?? '').replace(/\s*FEET\s*/i, '').trim(),
        })),
    });

    return (
        <FormPage
            url={'api/v1/cancel_plp/upload-batal-plp'}
            callbackPath={"/tps/plp/pembatalan"}
            formValid={false}
            model={model}
            layout="vertical"
            setModel={setModel}
            formSize="default"
            title={"Batal PLP"}
            showModePrefix={false}
            saveLabel="Send"
            showTopBack
            hideBottomCancel
            emptyModel={DefModel}
            onEditFormEvent={mapEditToForm}
            onSaveEvent={buildPayload}
            onClearModel={() => ({ ...DefModel, kemasan: [DefKemasan()], kontainer: [DefKontainer()] })}
        >
            <Row justify="end" style={{ marginBottom: 16 }}>
                <Button icon={<ThunderboltOutlined />} onClick={fillSample}>
                    Isi Otomatis (Contoh)
                </Button>
            </Row>
            {/* ---------- FORM HEADER ---------- */}
            <Typography.Title level={5} style={{ marginBottom: 16 }}>Form Header</Typography.Title>
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item label="Kode Kantor" name={'kodeKantor'} rules={[{ required: true }]}>
                        <Select
                            showSearch
                            placeholder="Pilih Kode Kantor"
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={(kdKantor || []).map((item) => ({
                                value: item.value,
                                label: `${item.value} - ${(item.name || '').trim()}`,
                            }))}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Tipe Data" name={'tipeData'} rules={[{ required: true }]}>
                        <Input autoComplete="off" placeholder="Masukkan Tipe Data" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Kode TPS" name={'kodeTps'} rules={[{ required: true }]}>
                        <Select
                            showSearch
                            placeholder="Pilih Kode TPS"
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={warehouseOpts}
                        />
                    </Form.Item>
                </Col>

                <Col span={8}>
                    <Form.Item label="Nomor PLP" name={'nomorPlp'} rules={[{ required: true }]}>
                        <Input autoComplete="off" placeholder="Masukkan Nomor PLP" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Tanggal PLP" required>
                        <DatePicker
                            style={{ width: '100%' }}
                            format={DATE_PICK_FMT}
                            placeholder="Select date"
                            value={model?.tanggalPlp ? moment(model.tanggalPlp) : null}
                            onChange={(date) => handleDateChange("tanggalPlp", date)}
                            allowClear={false}
                        />
                    </Form.Item>
                </Col>

                <Col span={8}>
                    <Form.Item label="Nomor Surat" name={'nomorSurat'} rules={[{ required: true, message: 'Nomor surat wajib diisi' }]}>
                        <Input autoComplete="off" placeholder="Masukkan Nomor Surat" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Tanggal Surat" required>
                        <DatePicker
                            style={{ width: '100%' }}
                            format={DATE_PICK_FMT}
                            placeholder="Select date"
                            value={model?.tanggalSurat ? moment(model.tanggalSurat) : null}
                            onChange={(date) => handleDateChange("tanggalSurat", date)}
                            allowClear={false}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Nama Pemohon" name={'namaPemohon'} rules={[{ required: true }]}>
                        <Input autoComplete="off" placeholder="Masukkan Nama Pemohon" onInput={e => e.target.value = e.target.value.toUpperCase()} />
                    </Form.Item>
                </Col>

                <Col span={8}>
                    <Form.Item label="Nomor BC 1.1" name={'nomorBc11'} rules={[{ required: true }]}>
                        <Input autoComplete="off" placeholder="Masukkan Nomor BC 1.1" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Tanggal BC 1.1" required>
                        <DatePicker
                            style={{ width: '100%' }}
                            format={DATE_PICK_FMT}
                            placeholder="Select date"
                            value={model?.tanggalBc11 ? moment(model.tanggalBc11) : null}
                            onChange={(date) => handleDateChange("tanggalBc11", date)}
                            allowClear={false}
                        />
                    </Form.Item>
                </Col>

                <Col span={24}>
                    <Form.Item label="Alasan" name={'alasan'} rules={[{ required: true, message: 'Alasan wajib diisi' }]}>
                        <Input.TextArea rows={3} autoComplete="off" placeholder="Masukkan Alasan Pembatalan PLP" />
                    </Form.Item>
                </Col>
            </Row>

            {/* ---------- FORM DATA KEMASAN ---------- */}
            <Card
                title="Form Data Kemasan"
                size="small"
                style={{ marginBottom: 16 }}
                extra={<Button size="small" icon={<PlusOutlined />} onClick={addKemasan}>Tambah Kemasan</Button>}
            >
                {(model.kemasan || []).map((k, idx) => (
                    <Row gutter={12} key={idx} align="bottom" style={{ marginBottom: 8 }}>
                        <Col span={4}>
                            <Form.Item label={idx === 0 ? 'Jenis Kemasan' : ''} required>
                                <Select
                                    showSearch
                                    value={k.jenisKemasan || undefined}
                                    placeholder="PK"
                                    optionFilterProp="label"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={kemasanOpts}
                                    onChange={(v) => updateKemasan(idx, { jenisKemasan: v })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item label={idx === 0 ? 'Jumlah Kemasan' : ''} required>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={1}
                                    value={k.jumlahKemasan}
                                    onChange={(v) => updateKemasan(idx, { jumlahKemasan: v })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={7}>
                            <Form.Item label={idx === 0 ? 'Nomor BL/AWB' : ''} required>
                                <Input
                                    value={k.nomorBlAwb}
                                    placeholder="406-93764506"
                                    autoComplete="off"
                                    onChange={(e) => updateKemasan(idx, { nomorBlAwb: e.target.value })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={7}>
                            <Form.Item label={idx === 0 ? 'Tanggal BL/AWB' : ''} required>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format={DATE_PICK_FMT}
                                    value={k.tanggalBlAwb ? moment(k.tanggalBlAwb) : null}
                                    onChange={(date) => updateKemasan(idx, { tanggalBlAwb: date })}
                                    allowClear={false}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={2}>
                            <Form.Item label={idx === 0 ? ' ' : ''}>
                                <Button danger icon={<DeleteOutlined />} onClick={() => removeKemasan(idx)} disabled={(model.kemasan || []).length <= 1} />
                            </Form.Item>
                        </Col>
                    </Row>
                ))}
            </Card>

            {/* ---------- FORM DATA KONTAINER ---------- */}
            <Card
                title="Form Data Kontainer"
                size="small"
                style={{ marginBottom: 8 }}
                extra={<Button size="small" icon={<PlusOutlined />} onClick={addKontainer}>Tambah Kontainer</Button>}
            >
                {(model.kontainer || []).map((k, idx) => (
                    <Row gutter={12} key={idx} align="bottom" style={{ marginBottom: 8 }}>
                        <Col span={11}>
                            <Form.Item label={idx === 0 ? 'Nomor Kontainer' : ''}>
                                <Input
                                    value={k.nomorKontainer}
                                    placeholder="Masukkan Nomor Kontainer"
                                    autoComplete="off"
                                    onInput={e => e.target.value = e.target.value.toUpperCase()}
                                    onChange={(e) => updateKontainer(idx, { nomorKontainer: e.target.value })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={11}>
                            <Form.Item label={idx === 0 ? 'Ukuran Kontainer' : ''}>
                                <Select
                                    value={k.ukuranKontainer || undefined}
                                    placeholder="Pilih Ukuran Kontainer"
                                    options={[
                                        { value: '20 FEET', label: '20 FEET' },
                                        { value: '40 FEET', label: '40 FEET' },
                                        { value: '45 FEET', label: '45 FEET' },
                                        { value: '60 FEET', label: '60 FEET' },
                                    ]}
                                    onChange={(v) => updateKontainer(idx, { ukuranKontainer: v })}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={2}>
                            <Form.Item label={idx === 0 ? ' ' : ''}>
                                <Button danger icon={<DeleteOutlined />} onClick={() => removeKontainer(idx)} disabled={(model.kontainer || []).length <= 1} />
                            </Form.Item>
                        </Col>
                    </Row>
                ))}
            </Card>
        </FormPage>
    )
}
